/**
 * Parse orchestration multi-intent envelope from WebSocket result `data`.
 * Server uses snake_case: primary_intent, intents; legacy intent aligns to primary.
 */

import type { IntentRecord } from "./types";

export interface MultiIntentFromData {
  /** Canonical primary (lowercased) for display / state */
  primaryIntent: string;
  /** Ordered list from server (may be empty) */
  intents: IntentRecord[];
  /** Use for INTENT_TYPES branching — same as primary when server is enriched */
  branchIntent: string;
}

function isIntentRecord(x: unknown): x is IntentRecord {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return typeof o.intent === "string";
}

/**
 * @param data - `message.data` from a WebSocket `type: "result"` message
 */
export function multiIntentFromMessageData(data: unknown): MultiIntentFromData {
  if (!data || typeof data !== "object") {
    return { primaryIntent: "", intents: [], branchIntent: "" };
  }
  const d = data as Record<string, unknown>;

  const rawPrimary =
    (typeof d.primary_intent === "string" && d.primary_intent) ||
    (typeof d.intent === "string" && d.intent) ||
    "";
  const primaryIntent = rawPrimary.trim().toLowerCase();
  const branchIntent = primaryIntent;

  let intents: IntentRecord[] = [];
  if (Array.isArray(d.intents)) {
    intents = d.intents
      .map((item, idx) => {
        if (typeof item === "string" && item.trim()) {
          return {
            intent: item.trim().toLowerCase(),
            priority: 0,
            role: idx === 0 ? "primary" : "secondary",
          } as IntentRecord;
        }
        if (isIntentRecord(item)) {
          return {
            intent: String(item.intent).trim().toLowerCase(),
            priority: typeof item.priority === "number" ? item.priority : Number(item.priority) || 0,
            role: typeof item.role === "string" ? item.role : idx === 0 ? "primary" : "secondary",
          };
        }
        return null;
      })
      .filter((x): x is IntentRecord => x != null);
  }

  return { primaryIntent, intents, branchIntent };
}
